import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

type ConfirmAction = 'ventas' | 'productos' | null;

export default function ConfiguracionPage() {
  const { user } = useAuth();

  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [cleaning, setCleaning] = useState(false);
  const [resultMsg, setResultMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const handleCleanVentas = async () => {
    setCleaning(true);
    setConfirmAction(null);
    setResultMsg(null);

    try {
      // Primero borrar detalle_ventas (por FK constraint)
      const { error: detErr } = await supabase
        .from('detalle_ventas')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');
      if (detErr) throw detErr;

      // Luego borrar ventas
      const { error: venErr } = await supabase
        .from('ventas')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');
      if (venErr) throw venErr;

      setResultMsg({
        text: 'Historial de ventas eliminado correctamente.',
        type: 'success',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setResultMsg({ text: `Error al limpiar ventas: ${message}`, type: 'error' });
    } finally {
      setCleaning(false);
    }
  };

  const handleCleanProductos = async () => {
    setCleaning(true);
    setConfirmAction(null);
    setResultMsg(null);

    try {
      // Borrar auditoria
      const { error: audErr } = await supabase
        .from('auditoria')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');
      if (audErr) throw audErr;

      // Borrar kardex
      const { error: karErr } = await supabase
        .from('kardex')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');
      if (karErr) throw karErr;

      setResultMsg({
        text: 'Historial de productos eliminado correctamente.',
        type: 'success',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setResultMsg({ text: `Error al limpiar historial de productos: ${message}`, type: 'error' });
    } finally {
      setCleaning(false);
    }
  };

  const getConfirmMessage = (): { title: string; detail: string } => {
    if (confirmAction === 'ventas') {
      return {
        title: 'Limpiar Historial de Ventas',
        detail: 'Se eliminarán permanentemente todas las ventas y sus detalles. Esta acción no se puede deshacer.',
      };
    }
    return {
      title: 'Limpiar Historial de Productos',
      detail: 'Se eliminarán permanentemente todos los registros de auditoría y movimientos de inventario de productos. Esta acción no se puede deshacer.',
    };
  };

  return (
    <Layout>
      <div className="fade-in space-y-6">
        <div>
          <h2 className="text-xl font-bold text-[#111827]">Configuración</h2>
          <p className="text-sm text-gray-500 mt-0.5">Ajustes del sistema</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#111827] mb-4">Información del Sistema</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Versión</span>
                <span className="text-sm font-medium text-[#111827]">AutoERP v1.0.0</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Base de Datos</span>
                <span className="text-sm font-medium text-[#16A34A] flex items-center gap-1">
                  <span className="w-2 h-2 bg-[#16A34A] rounded-full"></span>
                  Supabase (Conectado)
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Usuario Actual</span>
                <span className="text-sm font-medium text-[#111827]">{user?.nombre || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-500">Rol</span>
                <span className="text-sm font-medium text-[#16A34A] capitalize">{user?.rol === 'super_admin' ? 'Super Admin' : 'Usuario'}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#111827] mb-4">Estado de Conexiones</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Supabase</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#16A34A]/10 text-[#16A34A]">Conectado</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Almacenamiento</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#16A34A]/10 text-[#16A34A]">Disponible</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-500">Autenticación</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#16A34A]/10 text-[#16A34A]">Supabase Auth</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#16A34A]/5 border border-[#16A34A]/20 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-[#16A34A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-[#16A34A] w-4 h-4 flex items-center justify-center"></i>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[#111827]">Sistema Conectado</h4>
              <p className="text-sm text-gray-600 mt-1">
                Todas las funcionalidades del sistema están habilitadas. La base de datos PostgreSQL, autenticación y almacenamiento de archivos están operativos a través de Supabase.
              </p>
            </div>
          </div>
        </div>

        {/* Limpieza de Datos Históricos */}
        <div className="bg-white rounded-xl p-5 border border-[#EA7C5A]/30">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 bg-[#EA7C5A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-fill text-[#EA7C5A] w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-[#111827]">Limpieza de Datos Históricos</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Estas acciones eliminan permanentemente los datos históricos. Úsalas con precaución, no se pueden deshacer.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {/* Card: Limpiar Historial de Ventas */}
            <div className="flex items-center justify-between p-4 bg-[#EDF7ED] rounded-lg gap-4">
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-[#111827]">Limpiar Historial de Ventas</h4>
                <p className="text-xs text-gray-500 mt-0.5 truncate">Elimina todas las ventas y sus detalles</p>
              </div>
              <button
                onClick={() => setConfirmAction('ventas')}
                disabled={cleaning}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#EA7C5A] text-white rounded-lg text-sm font-medium hover:bg-[#D96B4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                <span>Limpiar Ventas</span>
              </button>
            </div>

            {/* Card: Limpiar Historial de Productos */}
            <div className="flex items-center justify-between p-4 bg-[#EDF7ED] rounded-lg gap-4">
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-[#111827]">Limpiar Historial de Productos</h4>
                <p className="text-xs text-gray-500 mt-0.5 truncate">Elimina todos los registros de auditoría y movimientos de inventario de productos</p>
              </div>
              <button
                onClick={() => setConfirmAction('productos')}
                disabled={cleaning}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#EA7C5A] text-white rounded-lg text-sm font-medium hover:bg-[#D96B4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                <span>Limpiar Historial</span>
              </button>
            </div>
          </div>

          {resultMsg && (
            <div className={`mt-4 p-3 rounded-lg text-sm ${
              resultMsg.type === 'success'
                ? 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20'
                : 'bg-[#DC2626]/10 text-[#DC2626] border border-[#DC2626]/20'
            }`}>
              {resultMsg.text}
            </div>
          )}
        </div>

        {/* Modal de Confirmación */}
        {confirmAction && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setConfirmAction(null)}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
                <div className="p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#EA7C5A]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-fill text-[#EA7C5A] w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#111827]">{getConfirmMessage().title}</h3>
                      <p className="text-sm text-gray-500">Esta acción no se puede deshacer</p>
                    </div>
                  </div>

                  <div className="bg-[#F4F6F8] rounded-xl p-4">
                    <p className="text-sm text-gray-700">
                      <strong className="text-[#DC2626]">{getConfirmMessage().detail}</strong>
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setConfirmAction(null)}
                      disabled={cleaning}
                      className="flex-1 py-2.5 bg-[#F4F6F8] text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => {
                        if (confirmAction === 'ventas') handleCleanVentas();
                        else handleCleanProductos();
                      }}
                      disabled={cleaning}
                      className="flex-1 py-2.5 bg-[#EA7C5A] text-white rounded-lg text-sm font-medium hover:bg-[#D96B4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                    >
                      {cleaning ? (
                        <>
                          <div className="spinner w-4 h-4 border-2 border-white border-t-transparent"></div>
                          <span>Eliminando...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                          <span>Sí, Eliminar</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}